/*
 * /intake/$patientId (page)
 */
import {
	Badge,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Container,
	Page,
	PageHeader,
	Placeholder,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { PatientIntakeForm } from "../../features/intakes/patient-intake-form"
import { trpc } from "../../trpc"

export const Route = createFileRoute("/intake/$patientId")({
	component: IntakePage,
})

function IntakePage() {
	const { patientId } = Route.useParams()

	const { data: patient, isLoading: patientLoading } =
		trpc.patient.getByFilter.useQuery({ where: { id: patientId } })

	const { data: existingIntake, isLoading: intakeLoading } =
		trpc.patientIntake.getByFilter.useQuery({
			where: { patientId, isLatest: true, active: true },
		})

	const isLoading = patientLoading || intakeLoading

	const patientName = patient ? `${patient.firstName} ${patient.lastName}` : null

	return (
		<Page>
			<Container width="xl" padding="lg" gap="lg">
				<PageHeader
					title={patientName ? `Intake Form — ${patientName}` : "Patient Intake"}
				/>

				{isLoading ? (
					<Placeholder title="Loading..." />
				) : (
					<>
						{/* Summary of existing intake */}
						{existingIntake && (
							<Card>
								<CardHeader>
									<div className="flex items-center justify-between">
										<div>
											<CardTitle>Current Intake</CardTitle>
											<CardDescription>
												Submitted{" "}
												{new Date(existingIntake.createdAt).toLocaleDateString()}
											</CardDescription>
										</div>
										<Badge variant="secondary">On File</Badge>
									</div>
								</CardHeader>
								<CardContent>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
										{existingIntake.familyDoctor && (
											<p>
												<span className="font-medium">Family Doctor: </span>
												{existingIntake.familyDoctor}
											</p>
										)}
										{existingIntake.occupation && (
											<p>
												<span className="font-medium">Occupation: </span>
												{existingIntake.occupation}
											</p>
										)}
										{existingIntake.maritalStatus && (
											<p>
												<span className="font-medium">Marital Status: </span>
												{existingIntake.maritalStatus === "married"
													? "Married / Common Law"
													: "Single"}
											</p>
										)}
										{existingIntake.allergies && (
											<p>
												<span className="font-medium">Allergies: </span>
												{existingIntake.allergies}
											</p>
										)}
										{existingIntake.familyDoctor && (
											<p>
												<span className="font-medium">Consent: </span>
												{existingIntake.consentSigned ? "Signed" : "Not signed"}
											</p>
										)}
									</div>
								</CardContent>
							</Card>
						)}

						{/* Form */}
						<Card>
							<CardHeader>
								<CardTitle>
									{existingIntake ? "Update Patient Intake" : "New Patient Intake"}
								</CardTitle>
								{existingIntake && (
									<CardDescription>
										Saving will create a new version and archive the current one.
									</CardDescription>
								)}
							</CardHeader>
							<CardContent>
								<PatientIntakeForm
									patientId={patientId}
									existingIntake={existingIntake ?? undefined}
								/>
							</CardContent>
						</Card>
					</>
				)}
			</Container>
		</Page>
	)
}
